import { IAppState } from '@core/state/state.service';
import { IEntitiesState } from './entity.reducer';
import { EntityKeys } from './entity-name-map';
import { createSelector, MemoizedSelector } from '@ngrx/store';
import { IEntityMap, IEntityState } from '@core/state/entities/entity';
import { IEntityKeyTypeMap } from '@core/state/entities/entities';
import { selectUISlice } from '@core/state/ui/ui.reducer';

export const selectEntitiesState = (state: IAppState): IEntitiesState =>
  state?.entities ?? ({} as IEntitiesState);

export const selectEntitySlice = <TKey extends EntityKeys>(key: TKey) =>
  createSelector(
    selectEntitiesState,
    (entities): IEntityState<IEntityKeyTypeMap[TKey]> =>
      entities?.[key] ?? ({ byId: {}, allIds: [] } as IEntityState<IEntityKeyTypeMap[TKey]>)
  );

export const selectEntityMap = <TKey extends keyof IEntitiesState>(key: TKey) =>
  createSelector(
    selectEntitySlice(key),
    (entitySlice: IEntityState<IEntityKeyTypeMap[TKey]>) => entitySlice.byId
  );

export const selectAllEntitiesGeneral = <TKey extends EntityKeys>(
  entitySliceMapSelector: MemoizedSelector<
    IAppState,
    IEntityState<IEntityKeyTypeMap[TKey]>
  >
) =>
  createSelector(
    entitySliceMapSelector,
    (
      entitySlice: IEntityState<IEntityKeyTypeMap[TKey]>
    ): IEntityKeyTypeMap[TKey][] =>
      entitySlice.allIds.map(
        (id) => entitySlice.byId[id] as IEntityKeyTypeMap[TKey]
      )
  );

export const selectAllEntities = <TKey extends EntityKeys>(key: TKey) =>
  selectAllEntitiesGeneral(selectEntitySlice(key));

export const selectEntityById = <
  TKey extends keyof IEntitiesState,
  TEntity extends IEntityKeyTypeMap[TKey],
>(
  key: TKey,
  id: string
) =>
  createSelector(
    selectEntityMap(key),
    (entityMap: IEntityMap<TEntity>): TEntity | null => entityMap[id] ?? null
  );
